
// Simple localStorage-backed cart for guests. Shape mirrors backend CartResponse.
const STORAGE_KEY = "guest_cart_v1";

function readRaw() {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    return t ? JSON.parse(t) : { items: [] };
  } catch {
    return { items: [] };
  }
}

function writeRaw(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data || { items: [] }));
  window.dispatchEvent(new CustomEvent("cart:updated"));
}

export function clearGuestCart() {
  writeRaw({ items: [] });
}

function priceOf(p) {
  const n = Number(p?.current_price ?? p?.retail_price ?? p?.unit_price ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function stockOf(p) {
  const n = Number(p?.stock_quantity ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function normalize(cart) {
  const items = (cart.items || []).map((it) => ({
    ...it,
    unit_price: Number(it.unit_price || 0),
    quantity: Number(it.quantity || 0),
    discount_amount: Number(it.discount_amount || 0),
    line_total: Number(it.line_total || 0),
  }));

  const subtotal = items.reduce((a, i) => a + Math.max(0, i.unit_price * i.quantity - i.discount_amount), 0);
  return {
    id: -1,
    user_id: 0,
    status: "ACTIVE",
    item_count: items.reduce((a, i) => a + i.quantity, 0),
    subtotal,
    discount_total: 0,
    tax_total: 0,
    grand_total: subtotal,
    items,
  };
}

export function getGuestCart() {
  return normalize(readRaw());
}

export function addGuestItem(product, qty = 1) {
  const cart = readRaw();
  const pid = Number(product?.id);
  if (!pid) return getGuestCart();

  const idx = cart.items.findIndex((i) => i.product_id === pid);
  const maxQty = stockOf(product) || 999999;

  if (idx >= 0) {
    cart.items[idx].quantity = Math.min(maxQty, Number(cart.items[idx].quantity || 0) + Number(qty || 1));
    cart.items[idx].line_total = Number(cart.items[idx].unit_price) * cart.items[idx].quantity - Number(cart.items[idx].discount_amount || 0);
  } else {
    const price = priceOf(product);
    const safeQty = Math.min(maxQty, Number(qty || 1));
    cart.items.push({
      id: `g-${pid}`,
      product_id: pid,
      product_name: product?.category ?? "Product",
      product_sku: product?.sku ?? null,
      unit_price: price,
      quantity: safeQty,
      discount_amount: 0,
      line_total: price * safeQty,
      // nice-to-have metadata for UI
      stock_quantity: stockOf(product),
      image_url: product?.image_url ?? null,
      brand: product?.brand ?? null,
    });
  }

  writeRaw(cart);
  return getGuestCart();
}

export function updateGuestItem(productId, qty) {
  const cart = readRaw();
  const idx = cart.items.findIndex((i) => i.product_id === Number(productId));
  if (idx === -1) return getGuestCart();

  const item = cart.items[idx];
  const maxQty = Number(item?.stock_quantity ?? 999999);
  const next = Math.max(1, Math.min(maxQty, Number(qty || 1)));
  item.quantity = next;
  item.line_total = Number(item.unit_price) * item.quantity - Number(item.discount_amount || 0);

  writeRaw(cart);
  return getGuestCart();
}

export function removeGuestItem(productId) {
  const cart = readRaw();
  const before = cart.items.length;
  cart.items = cart.items.filter((i) => i.product_id !== Number(productId));
  if (cart.items.length !== before) writeRaw(cart);
  return getGuestCart();
}
